import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState } from 'react';
import QRCode from 'qrcode';
import { getMachhaAsset } from '../../lib/machhaAsset';
import { getPortalUrl } from '../../lib/portal';
import styles from './QRPoster.module.css';

// ── Constants ──────────────────────────────────────────────────────────────────
const CANVAS_W = 1920;
const CANVAS_H = 1080;

// ── Theme System ──────────────────────────────────────────────────────────────
export type PosterTheme = 'indigo' | 'emerald' | 'rose' | 'amber' | 'slate';

export interface PosterThemeConfig {
  bgGradientStops: [string, string, string];
  accent: string;
  textPrimary: string;
  textSecondary: string;
  blobColors: string[];
  qrBg: string;
  qrFg: string;
}

export const POSTER_THEMES: Record<PosterTheme, PosterThemeConfig> = {
  indigo: {
    bgGradientStops: ['#450a0a', '#7f1d1d', '#0f172a'],
    accent: '#ef4444',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255,255,255,0.72)',
    blobColors: [
      'rgba(239,68,68,0.12)',
      'rgba(248,113,113,0.10)',
      'rgba(252,165,165,0.08)',
      'rgba(244,114,182,0.06)',
    ],
    qrBg: '#ffffff',
    qrFg: '#450a0a',
  },
  emerald: {
    bgGradientStops: ['#022c22', '#134e4a', '#0f172a'],
    accent: '#34d399',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255,255,255,0.72)',
    blobColors: [
      'rgba(52,211,153,0.12)',
      'rgba(45,212,191,0.10)',
      'rgba(20,184,166,0.08)',
      'rgba(110,231,183,0.06)',
    ],
    qrBg: '#ffffff',
    qrFg: '#022c22',
  },
  rose: {
    bgGradientStops: ['#4c0519', '#831843', '#1a0a14'],
    accent: '#fb7185',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255,255,255,0.72)',
    blobColors: [
      'rgba(251,113,133,0.12)',
      'rgba(244,114,182,0.10)',
      'rgba(236,72,153,0.08)',
      'rgba(251,146,60,0.06)',
    ],
    qrBg: '#ffffff',
    qrFg: '#4c0519',
  },
  amber: {
    bgGradientStops: ['#451a03', '#78350f', '#1c1003'],
    accent: '#fbbf24',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255,255,255,0.72)',
    blobColors: [
      'rgba(251,191,36,0.12)',
      'rgba(245,158,11,0.10)',
      'rgba(251,146,60,0.08)',
      'rgba(253,224,71,0.06)',
    ],
    qrBg: '#ffffff',
    qrFg: '#451a03',
  },
  slate: {
    bgGradientStops: ['#1e293b', '#334155', '#0f172a'],
    accent: '#94a3b8',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255,255,255,0.72)',
    blobColors: [
      'rgba(148,163,184,0.12)',
      'rgba(100,116,139,0.10)',
      'rgba(71,85,105,0.08)',
      'rgba(203,213,225,0.06)',
    ],
    qrBg: '#ffffff',
    qrFg: '#1e293b',
  },
};

export interface QRPosterHandle {
  getCanvas: () => HTMLCanvasElement | null;
}

interface QRPosterProps {
  albumName: string;
  eventName: string;
  inviteToken: string;
  theme?: PosterTheme;
  customText?: string;
}

/**
 * QRPoster — renders a 16:9 clay-themed poster on an HTML canvas.
 *
 * Content: gradient background, decorative clay blobs, Machha mascot (left),
 * QR code (right-center), event/album name, and instruction text.
 *
 * The canvas is always rendered at 1920x1080 for crisp PNG export; CSS scales
 * it down to fit its container via `width: 100%; height: auto`.
 */
export const QRPoster = forwardRef<QRPosterHandle, QRPosterProps>(
  ({ albumName, eventName, inviteToken, theme = 'indigo', customText }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [ready, setReady] = useState(false);

    useImperativeHandle(ref, () => ({
      getCanvas: () => canvasRef.current,
    }));

    useEffect(() => {
      let cancelled = false;

      async function draw() {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const t = POSTER_THEMES[theme];

        canvas.width = CANVAS_W;
        canvas.height = CANVAS_H;

        // ── 1. Background gradient ──────────────────────────────────────────
        const grad = ctx.createLinearGradient(0, 0, CANVAS_W, CANVAS_H);
        grad.addColorStop(0, t.bgGradientStops[0]);
        grad.addColorStop(0.45, t.bgGradientStops[1]);
        grad.addColorStop(1, t.bgGradientStops[2]);
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

        // ── 2. Decorative clay blobs ────────────────────────────────────────
        drawBlobs(ctx, t.blobColors);

        // ── 3. Subtle vignette ──────────────────────────────────────────────
        const vig = ctx.createRadialGradient(
          CANVAS_W / 2, CANVAS_H / 2, CANVAS_H * 0.3,
          CANVAS_W / 2, CANVAS_H / 2, CANVAS_W * 0.75,
        );
        vig.addColorStop(0, 'rgba(0,0,0,0)');
        vig.addColorStop(1, 'rgba(0,0,0,0.35)');
        ctx.fillStyle = vig;
        ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

        // ── 4. Load mascot + generate QR in parallel ────────────────────────
        const mascotUrl = getMachhaAsset('LandingPageAssets', 'cta-celebration-mascot', 4);
        const inviteUrl = `${getPortalUrl('viewer')}/join/${inviteToken}`;

        const [mascotImg, qrDataUrl] = await Promise.all([
          loadImage(mascotUrl).catch(() => null),
          QRCode.toDataURL(inviteUrl, {
            width: 420,
            margin: 2,
            color: { dark: t.qrFg, light: t.qrBg },
            errorCorrectionLevel: 'M',
          }),
        ]);

        if (cancelled) return;

        // ── 5. Draw mascot (left side) ──────────────────────────────────────
        if (mascotImg) {
          const mascotH = 460;
          const mascotW = (mascotImg.naturalWidth / mascotImg.naturalHeight) * mascotH;
          const mascotX = 100;
          const mascotY = CANVAS_H - mascotH - 80;

          // Soft glow behind mascot — use accent with alpha for glow
          ctx.save();
          ctx.shadowColor = hexToRgba(t.accent, 0.25);
          ctx.shadowBlur = 80;
          ctx.drawImage(mascotImg, mascotX, mascotY, mascotW, mascotH);
          ctx.restore();

          // Draw again without shadow for crisp image
          ctx.drawImage(mascotImg, mascotX, mascotY, mascotW, mascotH);
        }

        // ── 6. QR code with rounded-rect background (right side) ────────────
        const qrImg = await loadImage(qrDataUrl);
        if (cancelled) return;

        const qrSize = 340;
        const qrPad = 32;
        const qrBoxW = qrSize + qrPad * 2;
        const qrBoxH = qrSize + qrPad * 2;
        const qrBoxX = CANVAS_W - qrBoxW - 140;
        const qrBoxY = CANVAS_H / 2 - qrBoxH / 2 + 30;

        // Rounded rect background
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.3)';
        ctx.shadowBlur = 40;
        ctx.shadowOffsetY = 8;
        ctx.fillStyle = t.qrBg;
        roundRect(ctx, qrBoxX, qrBoxY, qrBoxW, qrBoxH, 28);
        ctx.fill();
        ctx.restore();

        // Inner highlight (clay feel)
        ctx.save();
        ctx.strokeStyle = 'rgba(255,255,255,0.5)';
        ctx.lineWidth = 2;
        roundRect(ctx, qrBoxX + 1, qrBoxY + 1, qrBoxW - 2, qrBoxH - 2, 27);
        ctx.stroke();
        ctx.restore();

        ctx.drawImage(qrImg, qrBoxX + qrPad, qrBoxY + qrPad, qrSize, qrSize);

        // ── 7. Text layout (right side, above QR) ───────────────────────────
        const textX = CANVAS_W - qrBoxW / 2 - 140;

        // Event name (smaller, above album name)
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillStyle = t.textSecondary;
        ctx.font = '500 28px Inter, system-ui, sans-serif';
        const eventTextY = qrBoxY - 100;
        ctx.fillText(truncateText(ctx, eventName, 560), textX, eventTextY);

        // Album name (large bold)
        ctx.fillStyle = t.textPrimary;
        ctx.font = '800 52px Inter, system-ui, sans-serif';
        const albumTextY = qrBoxY - 48;
        ctx.fillText(truncateText(ctx, albumName, 560), textX, albumTextY);

        // Accent underline
        const underlineW = Math.min(ctx.measureText(albumName).width, 560);
        ctx.strokeStyle = t.accent;
        ctx.lineWidth = 4;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(textX - underlineW / 2, albumTextY + 12);
        ctx.lineTo(textX + underlineW / 2, albumTextY + 12);
        ctx.stroke();

        // ── 8. Instruction text (below QR) ──────────────────────────────────
        ctx.fillStyle = t.textSecondary;
        ctx.font = '500 26px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        const instructionY = qrBoxY + qrBoxH + 48;
        const instructionText = customText?.trim() || 'Scan to view live photos';
        ctx.fillText(truncateText(ctx, instructionText, 560), textX, instructionY);

        // ── 9. Branding footer ──────────────────────────────────────────────
        ctx.fillStyle = 'rgba(255,255,255,0.3)';
        ctx.font = '400 20px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Powered by Pixel Machha', CANVAS_W / 2, CANVAS_H - 36);

        setReady(true);
      }

      draw();
      return () => { cancelled = true; };
    }, [albumName, eventName, inviteToken, theme, customText]);

    return (
      <canvas
        ref={canvasRef}
        className={`${styles['canvas']} ${ready ? styles['canvas-ready'] : styles['canvas-loading']}`}
      />
    );
  },
);

QRPoster.displayName = 'QRPoster';

// ── Helpers ────────────────────────────────────────────────────────────────────

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * Draw a rounded rectangle path (does not fill/stroke).
 */
function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number,
  w: number, h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/**
 * Truncate text with ellipsis if it exceeds maxWidth.
 */
function truncateText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let truncated = text;
  while (truncated.length > 0 && ctx.measureText(truncated + '...').width > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return truncated + '...';
}

/**
 * Convert a hex color string to an rgba string with the given alpha.
 */
function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * Draw decorative soft clay blobs for visual interest.
 */
function drawBlobs(ctx: CanvasRenderingContext2D, blobColors: string[]) {
  const blobs: { x: number; y: number; r: number; color: string }[] = [
    // Large blob top-right
    { x: CANVAS_W * 0.85, y: CANVAS_H * 0.08, r: 220, color: blobColors[0] },
    // Medium blob left-center
    { x: CANVAS_W * 0.05, y: CANVAS_H * 0.35, r: 160, color: blobColors[1] },
    // Small blob bottom-center
    { x: CANVAS_W * 0.55, y: CANVAS_H * 0.92, r: 130, color: blobColors[2] },
    // Tiny accent blob top-left
    { x: CANVAS_W * 0.2, y: CANVAS_H * 0.05, r: 80, color: blobColors[3] },
    // Medium blob right side
    { x: CANVAS_W * 0.95, y: CANVAS_H * 0.65, r: 180, color: blobColors[0] },
    // Accent near mascot
    { x: CANVAS_W * 0.15, y: CANVAS_H * 0.75, r: 100, color: blobColors[2] },
  ];

  for (const blob of blobs) {
    ctx.save();
    ctx.fillStyle = blob.color;
    ctx.beginPath();
    ctx.arc(blob.x, blob.y, blob.r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}
